import {createContext, ReactNode, useEffect, useState} from "react";
import {Optional} from "../utils/types.ts";
import {
  Organization,
  getOrganizations,
  switchOrganization as apiSwitchOrganization,
  createOrganization as apiCreateOrganization,
  updateOrganization as apiUpdateOrganization,
  deleteOrganization as apiDeleteOrganization
} from "../api/organizations.ts";
import {isAuthenticated} from "../utils/auth.ts";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
  SYSTEM_LOCAL_STORAGE
} from "../utils/localStorage.ts";
import {getEventBus, EventTypes, OrganizationPayload, MemberPayload} from "../services/EventBus.ts";

export const ORGANIZATION_SWITCHED_EVENT = 'organization-switched';
export const AUTH_STATE_CHANGED_EVENT = 'auth-state-changed';

export type OrganizationContextType = {
  organizations: Optional<Organization[]>;
  currentOrganization: Optional<Organization>;
  loading: boolean;
  error: Optional<string>;
  switchOrganization: (organizationId: number, newOrg?: Organization) => Promise<void>;
  createOrganization: (name: string) => Promise<Organization>;
  updateOrganization: (id: number, name: string) => Promise<void>;
  deleteOrganization: (id: number) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  isAdmin: () => boolean;
  hasEmrIntegration: () => boolean;
};

const contextDefaultValues: OrganizationContextType = {
  organizations: null,
  currentOrganization: null,
  loading: false,
  error: null,
  switchOrganization: async () => {},
  createOrganization: async () => ({} as Organization),
  updateOrganization: async () => {},
  deleteOrganization: async () => {},
  refreshOrganizations: async () => {},
  isAdmin: () => false,
  hasEmrIntegration: () => false,
};

const OrganizationContext = createContext<OrganizationContextType>(contextDefaultValues);

function OrganizationProvider({children}: {children: ReactNode}) {
  const [organizations, setOrganizations] = useState<Optional<Organization[]>>(null);
  const [currentOrganization, setCurrentOrganization] = useState<Optional<Organization>>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Optional<string>>(null);

  useEffect(() => {
    if(isAuthenticated() && !organizations) {
      fetchOrganizations();
    }
  }, []);

  // Listen for auth state changes (e.g., after signup/login) and refresh organizations
  useEffect(() => {
    function handleAuthStateChanged() {
      if(isAuthenticated()) {
        fetchOrganizations();
      } else {
        // User logged out, clear organization state
        setOrganizations(null);
        setCurrentOrganization(null);
        removeLocalStorageItem(SYSTEM_LOCAL_STORAGE.CURRENT_ORGANIZATION_ID);
      }
    }

    window.addEventListener(AUTH_STATE_CHANGED_EVENT, handleAuthStateChanged);
    return () => {
      window.removeEventListener(AUTH_STATE_CHANGED_EVENT, handleAuthStateChanged);
    };
  }, []);

  // Listen for real-time organization updates via WebSocket
  useEffect(() => {
    const eventBus = getEventBus();

    const orgUpdatedSub = eventBus.subscribe(EventTypes.ORGANIZATION_UPDATED, (payload: OrganizationPayload) => {
      // Update the organization in the list
      setOrganizations(prev => {
        if (!prev) return null;
        return prev.map(org =>
          org.id === payload.id
            ? { ...org, name: payload.name, slug: payload.slug }
            : org
        );
      });

      // Update current organization if it's the one that changed
      setCurrentOrganization(prev => {
        if (!prev || prev.id !== payload.id) return prev;
        return { ...prev, name: payload.name, slug: payload.slug };
      });
    });

    // When user is added to an org (including creating a new org), refresh the list
    const memberAddedSub = eventBus.subscribe(EventTypes.MEMBER_ADDED, (payload: MemberPayload) => {
      // Check if this org is already in our list
      setOrganizations(prev => {
        if (!prev) return prev;
        const orgExists = prev.some(org => org.id === payload.organization_id);
        if (!orgExists) {
          // New org we don't have - fetch the full list to get org details
          fetchOrganizations();
        }
        return prev;
      });
    });

    // When user is removed from an org, remove it from their list
    const memberRemovedSub = eventBus.subscribe(EventTypes.MEMBER_REMOVED, (payload: MemberPayload) => {
      // Get current user ID from localStorage
      const userJson = getLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER);
      if (!userJson) return;

      const user = JSON.parse(userJson);
      const currentUserId = user.id;

      // Only process if this is about the current user being removed
      if (payload.user_id === currentUserId) {
        setOrganizations(prev => {
          if (!prev) return prev;
          return prev.filter(org => org.id !== payload.organization_id);
        });

        // If removed from current org, switch to another
        setCurrentOrganization(prev => {
          if (prev?.id === payload.organization_id) {
            return null;
          }
          return prev;
        });
      }
    });

    return () => {
      orgUpdatedSub.unsubscribe();
      memberAddedSub.unsubscribe();
      memberRemovedSub.unsubscribe();
    };
  }, []);

  async function fetchOrganizations() {
    setLoading(true);
    setError(null);
    try {
      const response = await getOrganizations();
      setOrganizations(response);

      // Try to restore from localStorage or use first org
      const savedOrgId = getLocalStorageItem(SYSTEM_LOCAL_STORAGE.CURRENT_ORGANIZATION_ID);
      if(savedOrgId) {
        const savedOrg = response.find(o => o.id === Number(savedOrgId));
        if(savedOrg) {
          setCurrentOrganization(savedOrg);
        } else {
          // Saved org not found, use first one
          setCurrentOrganization(response[0] || null);
          if(response[0]) {
            setLocalStorageItem(SYSTEM_LOCAL_STORAGE.CURRENT_ORGANIZATION_ID, String(response[0].id));
          }
        }
      } else if(response.length > 0) {
        setCurrentOrganization(response[0]);
        setLocalStorageItem(SYSTEM_LOCAL_STORAGE.CURRENT_ORGANIZATION_ID, String(response[0].id));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }

  async function handleSwitchOrganization(organizationId: number, newOrg?: Organization) {
    // Call API to update session's current organization
    await apiSwitchOrganization(organizationId);

    // Find the org in the array, or use the provided newOrg (for newly created orgs)
    const org = newOrg || organizations?.find(o => o.id === organizationId);

    if (org) {
      // Update local state
      setCurrentOrganization(org);
    }
    setLocalStorageItem(SYSTEM_LOCAL_STORAGE.CURRENT_ORGANIZATION_ID, String(organizationId));

    // Dispatch event for other contexts to clear their data
    window.dispatchEvent(new CustomEvent(ORGANIZATION_SWITCHED_EVENT, {
      detail: {organizationId}
    }));
  }

  async function handleCreateOrganization(name: string): Promise<Organization> {
    const newOrg = await apiCreateOrganization(name);
    setOrganizations(prev => prev ? [...prev, newOrg] : [newOrg]);
    return newOrg;
  }

  async function handleUpdateOrganization(id: number, name: string) {
    const updatedOrg = await apiUpdateOrganization(id, name);
    setOrganizations(prev => {
      if(!prev) return null;
      return prev.map(o => o.id === id ? updatedOrg : o);
    });
    if(currentOrganization?.id === id) {
      setCurrentOrganization(updatedOrg);
    }
  }

  async function handleDeleteOrganization(id: number) {
    await apiDeleteOrganization(id);
    setOrganizations(prev => {
      if(!prev) return null;
      return prev.filter(o => o.id !== id);
    });

    // If we deleted the current org, switch to another one
    if(currentOrganization?.id === id && organizations) {
      const remaining = organizations.filter(o => o.id !== id);
      if(remaining.length > 0) {
        handleSwitchOrganization(remaining[0].id);
      } else {
        setCurrentOrganization(null);
        removeLocalStorageItem(SYSTEM_LOCAL_STORAGE.CURRENT_ORGANIZATION_ID);
      }
    }
  }

  function isAdmin(): boolean {
    return currentOrganization?.role === 'admin';
  }

  function hasEmrIntegration(): boolean {
    return currentOrganization?.emr_config?.enabled === true;
  }

  return <OrganizationContext.Provider value={{
    organizations,
    currentOrganization,
    loading,
    error,
    switchOrganization: handleSwitchOrganization,
    createOrganization: handleCreateOrganization,
    updateOrganization: handleUpdateOrganization,
    deleteOrganization: handleDeleteOrganization,
    refreshOrganizations: fetchOrganizations,
    isAdmin,
    hasEmrIntegration
  }}>{children}</OrganizationContext.Provider>;
}

export {OrganizationContext, OrganizationProvider};
